/**
 * Motion command recognition over the per-tick direction history.
 * ARCHITECTURE.md §4 (`src/input`), §6.2 (`input:command`).
 *
 * The recogniser is a pure, three-free state machine. It is fed ONE numpad direction per
 * player per tick (`push`) and asked once per tick whether a command completed
 * (`recognise`). It never allocates after construction: the history is an Int8Array ring,
 * every pattern is a flat Int32Array of 9-bit direction masks, and the returned command id
 * is an interned string from `COMMANDS`.
 *
 * Directions are FACING-RELATIVE numpad digits (see `snapshot.js: numpadDirection`), so 6 is
 * always "toward the opponent". When the fighter's facing flips the stored history is mirrored
 * (`mirrorHistory`) so a motion started before a cross-up still completes.
 *
 * ---------------------------------------------------------------------------
 * Leniency model — this is what makes the game feel like a fighting game.
 * ---------------------------------------------------------------------------
 * A motion is a list of STEPS, each a set of acceptable directions, matched by walking the
 * history backwards from the newest tick:
 *
 *   1. The newest tick must be an EDGE into the final step (in the set now, not in it last
 *      tick). This makes every command fire exactly once, on the tick it completes, and stops
 *      a held direction from re-triggering the same command every tick.
 *   2. A step absorbs any number of consecutive ticks (holding a direction is free).
 *   3. Up to `MAX_GAP_TICKS` ticks of unrelated direction are tolerated per step, which is
 *      what lets `2,5,6` register as a quarter-circle on a keyboard where the diagonal never
 *      appears.
 *   4. Each motion declares a `forbid` mask: a direction that invalidates the motion outright
 *      (going to back mid-qcf, for instance) rather than being swallowed as a gap.
 *   5. Several patterns may match the same tick. The winner is the highest `priority`, and
 *      among equal priorities the LONGEST match, so `qcf` always beats the `ff` that is
 *      embedded inside it.
 *
 * Sidestep taps (`ss-up` / `ss-down`) are release-triggered: up or down tapped and let go
 * within `TAP_TICKS`. Holding instead of tapping is a jump or a crouch, which is the standard
 * 3D-fighter discrimination and the reason the tap must fire on release.
 */

import { COMMAND_WINDOW_TICKS, toF60 } from '../core/index.js';

// ---------------------------------------------------------------------------
// Windows and thresholds (all in simulation ticks; 2 ticks == 1 f60 frame)
// ---------------------------------------------------------------------------

/** Ring capacity for the direction history. Power of two so indexing is a mask. */
export const CMD_HISTORY_TICKS = 128;
const HIST_MASK = CMD_HISTORY_TICKS - 1;

/** Double-tap window for `ff` / `bb` / `dd` (10 f60). */
export const DASH_WINDOW_TICKS = 20;
/** Rolling-motion window for `qcf` / `qcb` / strict `dp`. */
export const MOTION_WINDOW_TICKS = COMMAND_WINDOW_TICKS;
/** Window for the keyboard-friendly two-step `dp`; tighter, to limit accidental uppercuts. */
export const DP_SHORTCUT_WINDOW_TICKS = 12;
/** Ticks of unrelated direction tolerated between two steps of a motion. */
export const MAX_GAP_TICKS = 2;
/** Longest hold that still counts as a sidestep tap rather than a jump/crouch (7 f60). */
export const TAP_TICKS = 14;
/** Ticks a direction must be held to bank a charge (45 f60). */
export const CHARGE_TICKS = 90;
/** Ticks a banked charge survives after the direction is released (12 f60). */
export const CHARGE_GRACE_TICKS = 24;

// ---------------------------------------------------------------------------
// Direction sets, as 9-bit masks over numpad digits 1..9 (bit = digit - 1)
// ---------------------------------------------------------------------------

function dirMask(...dirs) {
  let m = 0;
  for (let i = 0; i < dirs.length; i++) m |= 1 << (dirs[i] - 1);
  return m;
}

const ALL_DIRS = 0x1ff;

const FWD = dirMask(6);
const BACK = dirMask(4);
const DOWN = dirMask(2);
const DOWN_FWD = dirMask(3);
const DOWN_BACK = dirMask(1);

/** Any direction with a forward component. */
const FWD_ANY = dirMask(6, 9, 3);
/** Any direction with a back component. */
const BACK_ANY = dirMask(4, 7, 1);
/** Any direction with a down component. */
const DOWN_ANY = dirMask(2, 1, 3);
/** Any direction with an up component. */
const UP_ANY = dirMask(8, 7, 9);

const NOT_FWD = ALL_DIRS & ~FWD_ANY;
const NOT_BACK = ALL_DIRS & ~BACK_ANY;
const NOT_DOWN = ALL_DIRS & ~DOWN_ANY;

/** Mirror table for a facing flip: 1<->3, 4<->6, 7<->9; 2, 5, 8 are self-mirrored. */
const MIRROR = Int8Array.from([0, 3, 2, 1, 6, 5, 4, 9, 8, 7]);

function inMask(dir, mask) {
  return dir > 0 && (mask & (1 << (dir - 1))) !== 0;
}

// ---------------------------------------------------------------------------
// Command table — the single source of truth for recognition AND for CONTROLS.md
// ---------------------------------------------------------------------------

/**
 * Every recognised command. `kind`:
 *   'motion' — a step sequence matched over the history.
 *   'tap'    — a direction tapped and released inside `window`.
 *   'charge' — a direction held for `threshold` ticks.
 *
 * Two entries may share an `id` (the strict and shortcut `dp`); both emit the same command.
 * `alt: true` marks the secondary spelling so documentation lists it as "also accepts".
 */
export const COMMAND_DEFS = Object.freeze([
  Object.freeze({
    id: 'dp', kind: 'motion', priority: 40, alt: false,
    label: 'Rising uppercut motion', notation: 'f,d,d/f',
    description: 'Forward, down, down-forward. The classic anti-air motion.',
    window: MOTION_WINDOW_TICKS, forbid: dirMask(4, 7),
    steps: Object.freeze([FWD_ANY, DOWN | DOWN_BACK, DOWN_FWD]),
  }),
  Object.freeze({
    id: 'dp', kind: 'motion', priority: 38, alt: true,
    label: 'Rising uppercut motion (shortcut)', notation: 'f~d/f',
    description: 'Forward straight into down-forward. Exists so keyboard players, who get '
      + 'd/f the instant they add the down key, are not locked out of the motion.',
    window: DP_SHORTCUT_WINDOW_TICKS, forbid: dirMask(4, 7, 1),
    steps: Object.freeze([FWD, DOWN_FWD]),
  }),
  Object.freeze({
    id: 'qcf', kind: 'motion', priority: 30, alt: false,
    label: 'Quarter-circle forward', notation: 'd,d/f,f',
    description: 'Down through down-forward to forward. Reaching forward completes it.',
    window: MOTION_WINDOW_TICKS, forbid: dirMask(4, 7, 8),
    steps: Object.freeze([DOWN | DOWN_BACK | DOWN_FWD, FWD]),
  }),
  Object.freeze({
    id: 'qcb', kind: 'motion', priority: 30, alt: false,
    label: 'Quarter-circle back', notation: 'd,d/b,b',
    description: 'Down through down-back to back.',
    window: MOTION_WINDOW_TICKS, forbid: dirMask(6, 9, 8),
    steps: Object.freeze([DOWN | DOWN_FWD | DOWN_BACK, BACK]),
  }),
  Object.freeze({
    id: 'ss-up', kind: 'tap', priority: 25, alt: false,
    label: 'Sidestep (background)', notation: 'u (tap)',
    description: 'Tap up and release inside 7 frames. Holding up jumps instead.',
    window: TAP_TICKS, tapMask: UP_ANY, pureDir: 8,
  }),
  Object.freeze({
    id: 'ss-down', kind: 'tap', priority: 25, alt: false,
    label: 'Sidestep (foreground)', notation: 'd (tap)',
    description: 'Tap down and release inside 7 frames. Holding down crouches instead.',
    window: TAP_TICKS, tapMask: DOWN_ANY, pureDir: 2,
  }),
  Object.freeze({
    id: 'ff', kind: 'motion', priority: 10, alt: false,
    label: 'Forward dash', notation: 'f,f',
    description: 'Forward, release, forward inside 10 frames.',
    window: DASH_WINDOW_TICKS, forbid: 0,
    steps: Object.freeze([FWD, NOT_FWD, FWD]),
  }),
  Object.freeze({
    id: 'bb', kind: 'motion', priority: 10, alt: false,
    label: 'Backdash', notation: 'b,b',
    description: 'Back, release, back inside 10 frames.',
    window: DASH_WINDOW_TICKS, forbid: 0,
    steps: Object.freeze([BACK, NOT_BACK, BACK]),
  }),
  Object.freeze({
    id: 'dd', kind: 'motion', priority: 10, alt: false,
    label: 'Crouch dash', notation: 'd,d',
    description: 'Down, release, down inside 10 frames.',
    window: DASH_WINDOW_TICKS, forbid: 0,
    steps: Object.freeze([DOWN, NOT_DOWN, DOWN]),
  }),
  Object.freeze({
    id: 'charge-back', kind: 'charge', priority: 1, alt: false,
    label: 'Back charge banked', notation: '[b]',
    description: 'Back held for 45 frames. Stays banked for 12 frames after release.',
    window: CHARGE_GRACE_TICKS, threshold: CHARGE_TICKS, holdMask: BACK_ANY,
  }),
  Object.freeze({
    id: 'charge-down', kind: 'charge', priority: 1, alt: false,
    label: 'Down charge banked', notation: '[d]',
    description: 'Down held for 45 frames. Stays banked for 12 frames after release.',
    window: CHARGE_GRACE_TICKS, threshold: CHARGE_TICKS, holdMask: DOWN_ANY,
  }),
]);

/** Unique command ids, in table order. Index is the compact encoding used by history rings. */
export const COMMANDS = Object.freeze(uniqueIds(COMMAND_DEFS));

function uniqueIds(defs) {
  const out = [];
  for (const d of defs) if (!out.includes(d.id)) out.push(d.id);
  return out;
}

/** Compact index for a command id, or -1. Used to store history without allocating strings. */
export function commandIndex(id) {
  return COMMANDS.indexOf(id);
}

/** Command id for a compact index, or '' when out of range. */
export function commandAt(index) {
  return index >= 0 && index < COMMANDS.length ? COMMANDS[index] : '';
}

/**
 * Deduplicated, display-ready description of every command. `accepts` lists every accepted
 * spelling of the motion. CONTROLS.md is generated from this; nothing here is hard-coded prose
 * that could drift from the recogniser.
 */
export function describeCommands() {
  const byId = new Map();
  for (const d of COMMAND_DEFS) {
    let row = byId.get(d.id);
    if (!row) {
      row = {
        command: d.id,
        kind: d.kind,
        label: d.label,
        notation: d.notation,
        description: d.description,
        windowTicks: d.window,
        windowF60: toF60(d.window),
        priority: d.priority,
        accepts: [],
      };
      byId.set(d.id, row);
    }
    row.accepts.push(d.notation);
    if (d.priority > row.priority) row.priority = d.priority;
  }
  return [...byId.values()];
}

// ---------------------------------------------------------------------------
// Compiled tables — flat, typed, allocation-free to walk
// ---------------------------------------------------------------------------

const MOTIONS = COMMAND_DEFS.filter((d) => d.kind === 'motion');
const MOTION_COUNT = MOTIONS.length;
const MOTION_ID = MOTIONS.map((d) => d.id);
const MOTION_PRIORITY = Int32Array.from(MOTIONS.map((d) => d.priority));
const MOTION_WINDOW = Int32Array.from(MOTIONS.map((d) => d.window));
const MOTION_FORBID = Int32Array.from(MOTIONS.map((d) => d.forbid));
const MOTION_STEP_OFF = new Int32Array(MOTION_COUNT);
const MOTION_STEP_LEN = new Int32Array(MOTION_COUNT);
const MOTION_STEPS = (() => {
  let total = 0;
  for (const d of MOTIONS) total += d.steps.length;
  const flat = new Int32Array(total);
  let off = 0;
  for (let i = 0; i < MOTION_COUNT; i++) {
    const steps = MOTIONS[i].steps;
    MOTION_STEP_OFF[i] = off;
    MOTION_STEP_LEN[i] = steps.length;
    for (let s = 0; s < steps.length; s++) flat[off + s] = steps[s];
    off += steps.length;
  }
  return flat;
})();

for (let i = 0; i < MOTION_COUNT; i++) {
  if (MOTION_STEP_LEN[i] < 2) {
    throw new Error(`motion "${MOTION_ID[i]}" needs at least 2 steps; single-step "motions" `
      + 'are just held directions and belong in the snapshot, not the recogniser.');
  }
}

const TAP_DEFS = COMMAND_DEFS.filter((d) => d.kind === 'tap');
const TAP_UP = TAP_DEFS.find((d) => d.id === 'ss-up');
const TAP_DOWN = TAP_DEFS.find((d) => d.id === 'ss-down');
const CHARGE_BACK_DEF = COMMAND_DEFS.find((d) => d.id === 'charge-back');
const CHARGE_DOWN_DEF = COMMAND_DEFS.find((d) => d.id === 'charge-down');

/** Charge slot ids accepted by `chargeTicks` / `hasCharge`. */
export const CHARGE_SLOTS = Object.freeze(['back', 'down']);

// ---------------------------------------------------------------------------
// The recogniser
// ---------------------------------------------------------------------------

export class CommandRecogniser {
  /** @param {number} [players] number of independent input slots */
  constructor(players = 2) {
    this.players = players;
    /** @type {Array<Int8Array>} newest direction lives at `_head - 1`. */
    this._hist = new Array(players);
    this._head = new Int32Array(players);
    this._filled = new Int32Array(players);

    // Tap tracking. `_upHeld` counts consecutive ticks with an up component; `_upPure`
    // records that the hold passed through pure up, so a diagonal hop is not a sidestep.
    this._upHeld = new Int32Array(players);
    this._downHeld = new Int32Array(players);
    this._upPure = new Uint8Array(players);
    this._downPure = new Uint8Array(players);
    this._tapUp = new Uint8Array(players);
    this._tapDown = new Uint8Array(players);
    this._tapUpSpan = new Int32Array(players);
    this._tapDownSpan = new Int32Array(players);

    // Charge tracking.
    this._chargeBack = new Int32Array(players);
    this._chargeDown = new Int32Array(players);
    this._chargeBackValid = new Int32Array(players);
    this._chargeDownValid = new Int32Array(players);
    this._chargeBackFired = new Uint8Array(players);
    this._chargeDownFired = new Uint8Array(players);

    /** Span in ticks of the match returned by the last `recognise` call. */
    this.lastSpan = 0;

    for (let p = 0; p < players; p++) this._hist[p] = new Int8Array(CMD_HISTORY_TICKS);
  }

  /** Clears one player's history, or every player when `player < 0`. */
  reset(player = -1) {
    const from = player < 0 ? 0 : player;
    const to = player < 0 ? this.players : player + 1;
    for (let p = from; p < to; p++) {
      this._hist[p].fill(0);
      this._head[p] = 0;
      this._filled[p] = 0;
      this._upHeld[p] = 0;
      this._downHeld[p] = 0;
      this._upPure[p] = 0;
      this._downPure[p] = 0;
      this._tapUp[p] = 0;
      this._tapDown[p] = 0;
      this._tapUpSpan[p] = 0;
      this._tapDownSpan[p] = 0;
      this._chargeBack[p] = 0;
      this._chargeDown[p] = 0;
      this._chargeBackValid[p] = 0;
      this._chargeDownValid[p] = 0;
      this._chargeBackFired[p] = 0;
      this._chargeDownFired[p] = 0;
    }
    this.lastSpan = 0;
  }

  /**
   * Records this tick's facing-relative numpad direction and advances tap/charge tracking.
   * Call exactly once per player per tick, before `recognise`.
   * @param {number} player
   * @param {number} dir numpad 1..9 (5 = neutral)
   */
  push(player, dir) {
    const p = player;

    // --- sidestep taps: detected on the release tick ---
    this._tapUp[p] = 0;
    this._tapDown[p] = 0;
    if (inMask(dir, UP_ANY)) {
      this._upHeld[p]++;
      if (dir === TAP_UP.pureDir) this._upPure[p] = 1;
    } else {
      const held = this._upHeld[p];
      if (held > 0 && held <= TAP_UP.window && this._upPure[p] === 1) {
        this._tapUp[p] = 1;
        this._tapUpSpan[p] = held;
      }
      this._upHeld[p] = 0;
      this._upPure[p] = 0;
    }
    if (inMask(dir, DOWN_ANY)) {
      this._downHeld[p]++;
      if (dir === TAP_DOWN.pureDir) this._downPure[p] = 1;
    } else {
      const held = this._downHeld[p];
      if (held > 0 && held <= TAP_DOWN.window && this._downPure[p] === 1) {
        this._tapDown[p] = 1;
        this._tapDownSpan[p] = held;
      }
      this._downHeld[p] = 0;
      this._downPure[p] = 0;
    }

    // --- charges ---
    this._chargeBackFired[p] = 0;
    this._chargeDownFired[p] = 0;
    if (inMask(dir, CHARGE_BACK_DEF.holdMask)) {
      this._chargeBack[p]++;
      if (this._chargeBack[p] === CHARGE_BACK_DEF.threshold) this._chargeBackFired[p] = 1;
      if (this._chargeBack[p] >= CHARGE_BACK_DEF.threshold) {
        this._chargeBackValid[p] = CHARGE_BACK_DEF.window;
      }
    } else {
      this._chargeBack[p] = 0;
      if (this._chargeBackValid[p] > 0) this._chargeBackValid[p]--;
    }
    if (inMask(dir, CHARGE_DOWN_DEF.holdMask)) {
      this._chargeDown[p]++;
      if (this._chargeDown[p] === CHARGE_DOWN_DEF.threshold) this._chargeDownFired[p] = 1;
      if (this._chargeDown[p] >= CHARGE_DOWN_DEF.threshold) {
        this._chargeDownValid[p] = CHARGE_DOWN_DEF.window;
      }
    } else {
      this._chargeDown[p] = 0;
      if (this._chargeDownValid[p] > 0) this._chargeDownValid[p]--;
    }

    // --- history ---
    const h = this._hist[p];
    h[this._head[p]] = dir;
    this._head[p] = (this._head[p] + 1) & HIST_MASK;
    if (this._filled[p] < CMD_HISTORY_TICKS) this._filled[p]++;
  }

  /**
   * Direction `back` ticks ago. `back = 0` is the tick most recently pushed.
   * @returns {number} numpad 1..9, or 0 when that tick has not been recorded
   */
  dirAt(player, back) {
    if (back < 0 || back >= this._filled[player]) return 0;
    return this._hist[player][(this._head[player] - 1 - back) & HIST_MASK];
  }

  /** Ticks of history recorded for this player, capped at `CMD_HISTORY_TICKS`. */
  depth(player) {
    return this._filled[player];
  }

  /**
   * Recognises the command that completed on the newest tick, if any.
   * Highest priority wins; equal priorities prefer the longest match.
   * @returns {string} an interned id from `COMMANDS`, or '' for none
   */
  recognise(player) {
    let bestId = '';
    let bestPriority = -1;
    let bestSpan = -1;

    for (let m = 0; m < MOTION_COUNT; m++) {
      const span = this._matchMotion(player, m);
      if (span < 0) continue;
      const pri = MOTION_PRIORITY[m];
      if (pri > bestPriority || (pri === bestPriority && span > bestSpan)) {
        bestPriority = pri;
        bestSpan = span;
        bestId = MOTION_ID[m];
      }
    }

    if (this._tapUp[player] === 1 && TAP_UP.priority > bestPriority) {
      bestPriority = TAP_UP.priority;
      bestSpan = this._tapUpSpan[player];
      bestId = TAP_UP.id;
    }
    if (this._tapDown[player] === 1 && TAP_DOWN.priority > bestPriority) {
      bestPriority = TAP_DOWN.priority;
      bestSpan = this._tapDownSpan[player];
      bestId = TAP_DOWN.id;
    }
    if (this._chargeBackFired[player] === 1 && CHARGE_BACK_DEF.priority > bestPriority) {
      bestPriority = CHARGE_BACK_DEF.priority;
      bestSpan = CHARGE_BACK_DEF.threshold;
      bestId = CHARGE_BACK_DEF.id;
    }
    if (this._chargeDownFired[player] === 1 && CHARGE_DOWN_DEF.priority > bestPriority) {
      bestPriority = CHARGE_DOWN_DEF.priority;
      bestSpan = CHARGE_DOWN_DEF.threshold;
      bestId = CHARGE_DOWN_DEF.id;
    }

    this.lastSpan = bestId === '' ? 0 : bestSpan;
    return bestId;
  }

  /**
   * Reverse-walks the history against motion `m`.
   * @returns {number} span in ticks of the completed match, or -1
   */
  _matchMotion(player, m) {
    const off = MOTION_STEP_OFF[m];
    const last = MOTION_STEP_LEN[m] - 1;
    const finalMask = MOTION_STEPS[off + last];

    // Rule 1: the newest tick must be a fresh edge into the final step.
    const d0 = this.dirAt(player, 0);
    if (!inMask(d0, finalMask)) return -1;
    if (inMask(this.dirAt(player, 1), finalMask)) return -1;

    const forbid = MOTION_FORBID[m];
    const filled = this._filled[player];
    let limit = MOTION_WINDOW[m];
    if (limit > filled - 1) limit = filled - 1;
    if (limit > CMD_HISTORY_TICKS - 1) limit = CMD_HISTORY_TICKS - 1;

    let step = last;
    let gaps = 0;
    for (let i = 1; i <= limit; i++) {
      const d = this._hist[player][(this._head[player] - 1 - i) & HIST_MASK];
      if (inMask(d, MOTION_STEPS[off + step - 1])) {
        step--;
        if (step === 0) return i;
        gaps = 0; // the gap budget is per step, not per motion
        continue;
      }
      if (inMask(d, MOTION_STEPS[off + step])) continue; // still holding this step
      if (inMask(d, forbid)) return -1;
      gaps++;
      if (gaps > MAX_GAP_TICKS) return -1;
    }
    return -1;
  }

  /**
   * Mirrors the stored history for a facing flip so a motion begun before a cross-up still
   * completes. The back charge is dropped because the key the player is holding has just
   * become "forward"; the down charge is mirror-invariant and survives.
   */
  mirrorHistory(player) {
    const h = this._hist[player];
    for (let i = 0; i < CMD_HISTORY_TICKS; i++) {
      const d = h[i];
      if (d > 0) h[i] = MIRROR[d];
    }
    this._chargeBack[player] = 0;
    this._chargeBackValid[player] = 0;
    this._chargeBackFired[player] = 0;
  }

  /** Ticks the given direction has been held. `which` is 'back' or 'down'. */
  chargeTicks(player, which) {
    return which === 'back' ? this._chargeBack[player] : this._chargeDown[player];
  }

  /** True while a charge is banked, including the post-release grace window. */
  hasCharge(player, which) {
    if (which === 'back') {
      return this._chargeBack[player] >= CHARGE_BACK_DEF.threshold
        || this._chargeBackValid[player] > 0;
    }
    return this._chargeDown[player] >= CHARGE_DOWN_DEF.threshold
      || this._chargeDownValid[player] > 0;
  }

  /** Ticks of post-release grace left on a banked charge. 0 when none. */
  chargeGrace(player, which) {
    return which === 'back' ? this._chargeBackValid[player] : this._chargeDownValid[player];
  }
}
